import React, { useEffect, useState } from "react";
import { brand } from "../../theme/theme";
import { Box, Typography, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { Avatar, AvatarFallback } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { AttachmentDisplay } from "./ThreadPost"; // reutilizar helper
import { formatContentWithMentions } from "../../components/CustomMentionsInput";
import { CustomTextArea } from "../../components/CustomTextArea";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/DropdownMenu";
import { ConfirmModal } from "../../components/ConfirmModal";
import { MoreVertical, Edit, Trash2, CheckCircle2, X, ThumbsUp } from "lucide-react";
import { LikersTooltip } from "../../components/LikersTooltip";
import {
  messageContentTypographySx,
  shortenLongUrlsInText,
  truncateMessageContent,
} from "./messageContentUtils";

const MAX_COMMENT_PREVIEW_CHARS = 300;

// Función helper para calcular tiempo relativo
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInSeconds = Math.floor((now - time) / 1000);
  const diffInDays = Math.floor(diffInSeconds / 86400);

  if (diffInSeconds < 60) return "hace un momento";
  if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} min`;
  if (diffInSeconds < 86400)
    return `hace ${Math.floor(diffInSeconds / 3600)} h`;

  // Menos de 7 días: mostrar en días
  if (diffInDays < 7) {
    return diffInDays === 1 ? "hace 1 día" : `hace ${diffInDays} días`;
  }

  // De 7 a 29 días: mostrar en semanas
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return weeks === 1 ? "hace 1 semana" : `hace ${weeks} semanas`;
  }

  // 30 días o más: mostrar en meses
  const months = Math.floor(diffInDays / 30);
  return months === 1 ? "hace 1 mes" : `hace ${months} meses`;
};

export function ThreadComment({
  comment,
  getRoleDisplayName,
  onDeleteAttachment,
  currentUser,
  parentMessageId,
  allowInteractions = true,
  onEditComment,
  onDeleteComment,
  onShowNotification,
  onLike,
  conversationFontSize = null, // Tamaño de letra personalizado en píxeles
}) {
  const theme = useTheme();
  const [showFullComment, setShowFullComment] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(comment.content || "");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helper para obtener el tamaño de letra a usar
  const getMessageFontSize = () => {
    if (conversationFontSize && typeof conversationFontSize === 'number') {
      const safeSize = Math.min(conversationFontSize, 16);
      return `${safeSize}px`;
    }
    return "0.85rem"; // Fallback al tamaño por defecto
  };

  const messageFontSize = getMessageFontSize();

  // Los datos del autor ahora vienen directamente en el comment del backend
  const author = {
    name: comment.author,
    role: comment.authorRole || "user", // NUEVO: incluir el rol del autor
    profileImage: comment.authorProfileImage || null, // NUEVO: imagen de perfil del autor
    initials:
      comment.author
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "U",
  };

  const commentPreview = truncateMessageContent(
    comment.content,
    MAX_COMMENT_PREVIEW_CHARS
  );
  const shouldTruncate = commentPreview.wasTruncated;
  const displayContent =
    shouldTruncate && !showFullComment
      ? shortenLongUrlsInText(commentPreview.text)
      : comment.content;
  const parentId = parentMessageId || comment.parent || comment.id;
  const currentUserNames = [
    currentUser?.full_name,
    currentUser?.name,
    currentUser?.fullName,
  ].filter(Boolean);
  const isOwnComment =
    !!currentUser &&
    (String(comment.authorId) === String(currentUser.id) ||
      (author.name && currentUserNames.includes(author.name)));
  const canManageComment =
    allowInteractions && isOwnComment && (onEditComment || onDeleteComment);

  // Formatear contenido con menciones
  const formatContent = (content) => {
    return formatContentWithMentions(content, theme);
  };

  useEffect(() => {
    setEditedContent(comment.content || "");
  }, [comment.content]);

  const handleStartEdit = (event) => {
    event?.stopPropagation();
    setIsEditing(true);
    setShowFullComment(true);
  };

  const handleCancelEdit = (event) => {
    event?.stopPropagation();
    setEditedContent(comment.content || "");
    setIsEditing(false);
  };

  const handleSaveEdit = async (event) => {
    event?.stopPropagation();
    const trimmedContent = (editedContent || "").trim();
    if (!trimmedContent) {
      alert("El comentario no puede estar vacío.");
      return;
    }

    if (!onEditComment) {
      return;
    }

    try {
      await onEditComment({
        commentId: comment.id,
        parentId,
        content: trimmedContent,
      });
      if (onShowNotification) {
        onShowNotification({
          message: "Comentario actualizado correctamente",
          isError: false,
        });
      }
      setIsEditing(false);
    } catch (error) {
      console.error("Error al actualizar comentario:", error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.detail ||
        "Error al actualizar el comentario. Intenta nuevamente.";
      if (onShowNotification) {
        onShowNotification({ message: errorMessage, isError: true });
      } else {
        alert(errorMessage);
      }
    }
  };

  const handleDeleteComment = async (event) => {
    event?.stopPropagation();
    if (!onDeleteComment) {
      return;
    }

    setShowDeleteModal(true);
  };

  const handleConfirmDeleteComment = async () => {
    setIsDeleting(true);
    try {
      await onDeleteComment({
        commentId: comment.id,
        parentId,
      });
      setShowDeleteModal(false);
      if (onShowNotification) {
        onShowNotification({
          message: "Comentario eliminado correctamente",
          isError: false,
        });
      }
    } catch (error) {
      console.error("Error al eliminar comentario:", error);
      const errorMessage =
        error?.message ||
        error?.response?.data?.detail ||
        "Error al eliminar el comentario. Intenta nuevamente.";
      setShowDeleteModal(false);
      if (onShowNotification) {
        onShowNotification({ message: errorMessage, isError: true });
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Modal de confirmación para eliminar comentario
  return (
    <>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          display: "flex",
          gap: "8px",
          marginBottom: "12px",
          borderBottom: `1px solid rgba(0, 0, 0, 0.06)`,
          "&:last-child": {
            marginBottom: 0,
            paddingBottom: 0,
            borderBottom: "none",
          },
        }}
      >
        <Avatar
          sx={{ width: 28, height: 28 }}
          src={author.profileImage || undefined}
        >
          <AvatarFallback
            sx={{
              backgroundColor: brand.avatar,
              color: "#fff",
              fontSize: "0.7rem",
              fontWeight: 600,
            }}
          >
            {author.initials}
          </AvatarFallback>
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              width: "100%",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "4px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  {author.name || "Usuario"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.7rem",
                  }}
                >
                  {getRelativeTime(comment.timestamp)}
                </Typography>
              </Box>

              {/* Contenido del comentario */}
              {isEditing ? (
                <Box
                  data-prevent-expand="true"
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    marginBottom: "8px",
                  }}
                >
                  <CustomTextArea
                    value={editedContent}
                    onChange={(event) => setEditedContent(event.target.value)}
                    placeholder="Edita tu comentario..."
                    autoFocus
                    rows={1}
                    maxRows={5}
                    autoExpand={true}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        handleCancelEdit(event);
                      }
                    }}
                    sx={{
                      flex: 1,
                      minHeight: "32px",
                      lineHeight: 1.4,
                      fontSize: "0.85rem",
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      paddingTop: "6px",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={handleSaveEdit}
                      title="Guardar"
                    >
                      <CheckCircle2 size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      title="Cancelar"
                    >
                      <X size={16} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Box
                  translate="no"
                  sx={{
                    marginBottom: "1px",
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <Typography
                    component="div"
                    variant="body2"
                    sx={{
                      ...messageContentTypographySx,
                      fontSize: messageFontSize,
                      display: "inline",
                    }}
                  >
                    {formatContent(displayContent)}
                  </Typography>
                  {shouldTruncate && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFullComment(!showFullComment);
                      }}
                      sx={{
                        padding: "2px 6px",
                        fontSize: "0.75rem",
                        color: theme.palette.primary.main,
                        marginLeft: "6px",
                        minWidth: "auto",
                        display: "inline-flex",
                        verticalAlign: "baseline",
                        "&:hover": {
                          backgroundColor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {showFullComment ? "Mostrar menos" : "Mostrar completo"}
                    </Button>
                  )}
                </Box>
              )}

              {comment.attachments && comment.attachments.length > 0 && (
                <Box sx={{ mt: 1, mb: 1.5 }}>
                  {comment.attachments.map((att) => (
                    <AttachmentDisplay
                      key={att.id}
                      attachment={{
                        ...att,
                        messageId: comment.id, // Usar el ID del comentario, no del padre
                        message_id: comment.id, // También en snake_case para compatibilidad
                      }}
                      canDelete={
                        currentUser &&
                        String(currentUser.id) === String(comment.authorId)
                      }
                      onDelete={(removedAttachmentId) => {
                        if (onDeleteAttachment) {
                          onDeleteAttachment({
                            messageId: comment.id,
                            attachmentId: removedAttachmentId,
                            parentId,
                          });
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
              {/* Acciones del comentario (Likes) */}
              {allowInteractions && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: 0.5, mb: "7px" }}>
                  <LikersTooltip likers={comment.liked_by}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLike && onLike(comment.id);
                      }}
                      sx={{
                        padding: "1px 6px",
                        gap: "4px",
                        minWidth: "auto",
                        height: "22px",
                        color: comment.is_liked
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <ThumbsUp
                        size={12}
                        fill={comment.is_liked ? theme.palette.primary.main : "none"}
                      />
                      {comment.like_count > 0 && <span>{comment.like_count}</span>}
                    </Button>
                  </LikersTooltip>
                </Box>
              )}
            </Box>

            {canManageComment && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    size="small"
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      width: "28px",
                      height: "28px",
                      padding: 0,
                      opacity: 0.6,
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <MoreVertical size={14} />
                  </IconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  onClick={(event) => event.stopPropagation()}
                  sx={{ minWidth: "150px" }}
                >
                  {onEditComment && (
                    <DropdownMenuItem
                      onClick={handleStartEdit}
                      disabled={isEditing}
                    >
                      <Edit size={14} />
                      Editar
                    </DropdownMenuItem>
                  )}
                  {onDeleteComment && (
                    <DropdownMenuItem
                      onClick={handleDeleteComment}
                      className="text-destructive"
                    >
                      <Trash2 size={14} />
                      Eliminar
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </Box>
        </Box>
      </Box>

      {/* Modal de confirmación para eliminar comentario */}
      <ConfirmModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDeleteComment}
        title="Eliminar comentario"
        question="¿Estás seguro de que quieres eliminar este comentario?"
        description="Esta acción no se puede deshacer. Se eliminará permanentemente el comentario."
        confirmLabel="Eliminar comentario"
        loadingLabel="Eliminando..."
        loading={isDeleting}
        tone="danger"
      />
    </>
  );
}
